import React from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const OffersSummary = () => {
    const offersSummary = useSelector((state) => state.posReportshiftSlice.shiftReportOffersSummary);
    const tableLoading = useSelector((state) => state.mainDashBoardSlice.tableLoadingGlobal);
    const decimalPosition = localStorage.getItem("decimalPosition") || 2;
    const currencySymbol = useSelector((state) => state.mainDashBoardSlice.currencyDetails?.symbol) || '₹';

    const normalizeOfferCode = (code) => {
        const raw = String(code || '').trim();
        if (!raw) return '';
        const upper = raw.toUpperCase();
        if (upper === '-' || upper === 'NIL' || upper === '-NIL-' || upper === 'NIL-' || upper === '-NIL') return '';
        if (/^-+$/.test(raw)) return '';
        return raw;
    };

    const toNumber = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
    };

    const formatMoney = (val) => {
        return toNumber(val).toFixed(decimalPosition);
    };

    const offerAppliedOrders = (offersSummary?.orders || []).filter((order) => {
        const offerCode = normalizeOfferCode(order?.offerCode);
        return Boolean(offerCode);
    });

    // Helper function to calculate discount type and value
    const getDiscountDetails = (order) => {
        const discount = order.discountAmount || 0;
        const orderValue = (order.subTotal || 0) + (order.totalExclusive || 0);
        const deliveryCharge = order.deliveryCharge || 0;
        const km = order.selfDeliveryKm || 0;
        const promoType = order.promoType;
        const offerCode = order.offerCode;
        
        // If promo type is set, use it
        if (promoType === 'PERCENTAGE') {
            const percentage = orderValue > 0 ? ((discount / orderValue) * 100).toFixed(1) : 0;
            return { type: 'Percentage', value: `${percentage}%`, code: offerCode };
        }
        if (promoType === 'FIXED_AMOUNT') {
            return { type: 'Fixed Amount', value: `${currencySymbol}${discount.toFixed(decimalPosition)}`, code: offerCode };
        }
        if (promoType === 'FREE_DELIVERY') {
            return { type: 'Free Delivery', value: km > 0 ? `${km} KM` : `${currencySymbol}${deliveryCharge}`, code: offerCode };
        }
        if (promoType === 'BUY_X_GET_Y') {
            return { type: 'Buy X Get Y', value: 'Combo', code: offerCode };
        }
        
        // Calculate type from values if no promo type
        if (discount > 0 && orderValue > 0) {
            const percentage = (discount / orderValue) * 100;
            // If percentage is a round number (like 5, 10, 15, 20), likely percentage discount
            if (percentage % 5 === 0 || Math.abs(percentage - Math.round(percentage)) < 0.1) {
                return { type: 'Percentage', value: `${percentage.toFixed(1)}%`, code: offerCode || '-' };
            }
            // Otherwise fixed amount
            return { type: 'Fixed Amount', value: `${currencySymbol}${discount.toFixed(decimalPosition)}`, code: offerCode || '-' };
        }
        
        // Free delivery check
        if (order.ordType === 'DELIVERY' && deliveryCharge === 0 && km > 0) {
            return { type: 'Free Delivery', value: `${km} KM`, code: offerCode || '-' };
        }
        
        return { type: 'Discount', value: `${currencySymbol}${discount.toFixed(decimalPosition)}`, code: offerCode || '-' };
    };

    // Calculate totals
    const totalOrders = offerAppliedOrders.length || 0;
    const totalDiscountAmount = offerAppliedOrders.reduce((sum, order) => sum + (Number(order?.discountAmount ?? order?.discountAmt ?? 0) || 0), 0) || 0;
    const totalOrderAmount = offerAppliedOrders.reduce((sum, order) => sum + (Number(order?.amtTotal ?? 0) || 0), 0) || 0;

    // Group by discount type
    const offerGroups = offerAppliedOrders.reduce((acc, order) => {
        const details = getDiscountDetails(order);
        const key = details.code !== '-' ? details.code : details.type;
        if (!acc[key]) {
            acc[key] = {
                code: details.code,
                type: details.type,
                count: 0,
                totalDiscount: 0,
                totalAmount: 0
            };
        }
        acc[key].count += 1;
        acc[key].totalDiscount += Number(order?.discountAmount ?? order?.discountAmt ?? 0) || 0;
        acc[key].totalAmount += Number(order?.amtTotal ?? 0) || 0;
        return acc;
    }, {}) || {};

    return (
        <div className="shift-report-table-container" style={{ padding: '20px' }}>
            {tableLoading ? (
                <div style={{ padding: '20px' }}>
                    <Skeleton variant="rectangular" height={40} style={{ marginBottom: '10px' }} />
                    <Skeleton variant="rectangular" height={200} />
                </div>
            ) : offerAppliedOrders.length > 0 ? (
                <>
                    {/* Summary Cards */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '20px', 
                        marginBottom: '20px',
                        flexWrap: 'wrap'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: '12px',
                            padding: '20px',
                            color: 'white',
                            minWidth: '200px',
                            flex: 1
                        }}>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Orders with Offers</div>
                            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{totalOrders}</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            borderRadius: '12px',
                            padding: '20px',
                            color: 'white',
                            minWidth: '200px',
                            flex: 1
                        }}>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Discount Given</div>
                            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{currencySymbol}{formatMoney(totalDiscountAmount)}</div>
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            borderRadius: '12px',
                            padding: '20px',
                            color: 'white',
                            minWidth: '200px',
                            flex: 1
                        }}>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Order Value</div>
                            <div style={{ fontSize: '28px', fontWeight: 'bold' }}>{currencySymbol}{formatMoney(totalOrderAmount)}</div>
                        </div>
                    </div>

                    {/* Offers Breakdown */}
                    <div style={{ 
                        background: '#f8f9fa', 
                        borderRadius: '8px', 
                        padding: '15px',
                        marginBottom: '20px'
                    }}>
                        <h4 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <LocalOfferIcon style={{ color: '#667eea' }} />
                            Offers Breakdown
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            {Object.values(offerGroups).map((group, idx) => (
                                <div key={idx} style={{
                                    background: 'white',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    border: '1px solid #e0e0e0',
                                    minWidth: '180px'
                                }}>
                                    <div style={{ 
                                        fontWeight: 'bold', 
                                        color: '#667eea',
                                        fontFamily: 'monospace',
                                        fontSize: '14px'
                                    }}>
                                        {group.code}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        {group.type}
                                    </div>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        marginTop: '8px',
                                        fontSize: '13px'
                                    }}>
                                        <span>Orders: <strong>{group.count}</strong></span>
                                        <span style={{ color: '#4CAF50' }}>
                                            -{currencySymbol}{formatMoney(group.totalDiscount)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Orders Table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f5f5f5' }}>
                                    <th style={thStyle}>Order Date</th>
                                    <th style={thStyle}>Order Ref</th>
                                    <th style={thStyle}>Customer</th>
                                    <th style={thStyle}>Offer Code</th>
                                    <th style={thStyle}>Offer Type</th>
                                    <th style={thStyle}>Order Amount</th>
                                    <th style={thStyle}>Discount</th>
                                    <th style={thStyle}>Paid Amount</th>
                                    <th style={thStyle}>Payment Method</th>
                                </tr>
                            </thead>
                            <tbody>
                                {offerAppliedOrders.map((order, index) => {
                                    const details = getDiscountDetails(order);
                                    return (
                                        <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={tdStyle}>{order.orderDate || '-'}</td>
                                            <td style={tdStyle}>{order.orderRef || order.name || '-'}</td>
                                            <td style={tdStyle}>{order.customerName || 'Walk-in'}</td>
                                            <td style={{ ...tdStyle, fontFamily: 'monospace', color: '#667eea', fontWeight: 'bold' }}>
                                                {details.code}
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    background: details.type === 'Percentage' ? '#e3f2fd' : 
                                                               details.type === 'Fixed Amount' ? '#f3e5f5' : 
                                                               details.type === 'Free Delivery' ? '#fff3e0' : '#e8f5e9',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px'
                                                }}>
                                                    {details.type}: {details.value}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                {currencySymbol}{formatMoney(order?.amtTotal)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right', color: '#4CAF50', fontWeight: 'bold' }}>
                                                -{currencySymbol}{formatMoney(order?.discountAmount ?? order?.discountAmt)}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                {currencySymbol}{formatMoney(order?.paidAmt)}
                                            </td>
                                            <td style={tdStyle}>{order.paymentMethod || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                    <td style={tdStyle} colSpan={5}>TOTAL</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        {currencySymbol}{formatMoney(totalOrderAmount)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#4CAF50' }}>
                                        -{currencySymbol}{formatMoney(totalDiscountAmount)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        {currencySymbol}{formatMoney(toNumber(totalOrderAmount) - toNumber(totalDiscountAmount))}
                                    </td>
                                    <td style={tdStyle}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </>
            ) : (
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '300px',
                    color: '#999'
                }}>
                    <LocalOfferIcon style={{ fontSize: '60px', marginBottom: '16px', opacity: 0.5 }} />
                    <p style={{ fontSize: '16px' }}>No offers applied in this shift</p>
                </div>
            )}
        </div>
    );
};

const thStyle = {
    padding: '12px 8px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
    fontSize: '13px',
    fontWeight: '600'
};

const tdStyle = {
    padding: '10px 8px',
    fontSize: '13px'
};

export default OffersSummary;
